import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Subject } from 'rxjs';
import * as tus from 'tus-js-client';

import { environment } from 'src/environments/environment';
import { AuthService } from '#services/auth/auth.service';
import { UploadQueueService } from '#services/upload-queue/upload-queue.service';
import { UploadMediaItem, UploadBatch, UploadStatus } from '#models/db/asset/upload.model';
import { BucketName } from '#models/db/asset/asset.model';
import { FriendlyErrorMessage } from '#models/error/error-message.model';
import { LocalNotificationService } from '#services/local-notification/local-notification.service';
import { ToastService } from '#services/toast/toast.service';
import { MemoryAssetsService } from '#services/memory-assets/memory-assets.service';
import { MemoryAsset } from '#models/db/memory/memory-asset.model';
import { ProfileService } from '#services/profile/profile.service';
import { LyfeVaultService } from '#services/lyfe-vault/lyfe-vault.service';
import { LyfeVaultDocument } from '#models/db/lyfe-vault/lyfe-vault.model';
import { Profile } from '#models/db/profile/profile.model';
import { FileSystemService } from '#services/file-system/file-system.service';
import { SupabaseService } from '#services/supabase/supabase.service';
import { AssetRepositoryService } from '#repositories/asset-repository/asset-repository.service';
import { logger } from '#lib/logger';
import { delay } from '#helpers/delay.helper';

interface ConcurrencyLimits {
  images: number;
  videos: number;
}

const NATIVE_LIMITS: ConcurrencyLimits = { images: 3, videos: 1 };
const WEB_LIMITS: ConcurrencyLimits = { images: 5, videos: 2 };
const RETRY_DELAYS = [2000, 5000, 15000];

@Injectable({
  providedIn: 'root',
})
export class UploadManagerService {
  uploads$ = new BehaviorSubject<UploadMediaItem[]>([]);
  isProcessing$ = new BehaviorSubject<boolean>(false);
  progressPercentage$ = new BehaviorSubject<string | null>(null);
  batchCompleted$ = new Subject<UploadBatch>();

  private activeUploads = new Map<number, tus.Upload>();
  private cancelledIds = new Set<number>();
  private activeImageCount = 0;
  private activeVideoCount = 0;
  private processing = false;

  private get limits(): ConcurrencyLimits {
    return Capacitor.isNativePlatform() ? NATIVE_LIMITS : WEB_LIMITS;
  }

  private permissionRequested = false;

  constructor(
    private authService: AuthService,
    private queue: UploadQueueService,
    private supabaseClient: SupabaseService,
    private localNotificationService: LocalNotificationService,
    private toastService: ToastService,
    private injector: Injector
  ) {}

  async processQueue(bucketName?: BucketName): Promise<void> {
    if (this.processing) return;

    this.processing = true;
    this.isProcessing$.next(true);

    // Ensure auth token is fresh before starting uploads to prevent
    // isAcquireTimeout errors after long image-processing delays
    await this.authService.hasValidAccessToken();

    // Request notification permission lazily on first upload batch
    if (!this.permissionRequested && Capacitor.isNativePlatform()) {
      this.permissionRequested = true;
      this.localNotificationService.requestPermission();
    }

    try {
      await this.runQueue(bucketName);
    } finally {
      this.processing = false;
      this.isProcessing$.next(false);
      this.progressPercentage$.next(null);
    }
  }

  async retryUpload(id: number): Promise<void> {
    const canRetry = await this.queue.incrementRetry(id);

    if (canRetry) {
      await this.refreshUploads();
      await this.processQueue();
    }
  }

  async retryAllFailed(): Promise<void> {
    const failed = await this.queue.getFailedItems();

    for (const item of failed) {
      if (item.id) {
        await this.queue.incrementRetry(item.id);
      }
    }

    await this.refreshUploads();
    await this.processQueue();
  }

  async clearUploads(): Promise<void> {
    const items = await this.queue.getAll();

    for (const item of items) {
      if (
        item.id &&
        (item.status === UploadStatus.Failed || item.status === UploadStatus.Cancelled)
      ) {
        await this.queue.remove(item.id);
      }
    }

    this.uploads$.next([]);
  }

  async clearStaleItems(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (this.processing) {
      await this.clearUploads();
      return;
    }

    await this.queue.clearAll();
    this.uploads$.next([]);
  }

  async cancelUpload(id: number): Promise<void> {
    this.cancelledIds.add(id);

    const tusUpload = this.activeUploads.get(id);

    if (tusUpload) {
      tusUpload.abort();
      this.activeUploads.delete(id);

      const items = this.uploads$.value;
      const item = items.find((i) => i.id === id);

      if (item) {
        this.decrementActiveCount(item);
      }
    }

    await this.queue.remove(id);
    await this.refreshUploads();
  }

  async cancelBatch(batchId: string): Promise<void> {
    const items = await this.queue.getByBatchId(batchId);

    for (const item of items) {
      if (item.id) {
        await this.cancelUpload(item.id);
      }
    }
  }

  async removeUpload(id: number): Promise<void> {
    await this.queue.remove(id);
    await this.refreshUploads();
  }

  private async runQueue(bucketName?: BucketName): Promise<void> {
    while (true) {
      let items = await this.queue.getAll();

      if (bucketName) {
        items = items.filter((item) => item.metadata.bucketName === bucketName);
      }

      await this.refreshUploads(items);
      this.updateAggregateProgress(items);

      const pending = items
        .filter((item) => item.status === UploadStatus.Pending)
        .sort((a, b) => {
          // Images before videos
          const aIsVideo = a.metadata.fileType.startsWith('video/') ? 1 : 0;
          const bIsVideo = b.metadata.fileType.startsWith('video/') ? 1 : 0;

          if (aIsVideo !== bIsVideo) return aIsVideo - bIsVideo;
          // Smaller files first
          if ((a.fileSize ?? 0) !== (b.fileSize ?? 0)) return (a.fileSize ?? 0) - (b.fileSize ?? 0);
          // FIFO
          return (a.addedAt ?? 0) - (b.addedAt ?? 0);
        });

      if (pending.length === 0 && this.activeUploads.size === 0) {
        break;
      }

      if (pending.length === 0) {
        // Wait for active uploads to finish
        await delay(500);
        continue;
      }

      // Launch as many uploads as concurrency allows
      let launched = false;

      for (const item of pending) {
        const isVideo = item.metadata.fileType.startsWith('video/');

        if (isVideo && this.activeVideoCount >= this.limits.videos) continue;
        if (!isVideo && this.activeImageCount >= this.limits.images) continue;

        if (item.id) {
          if (isVideo) this.activeVideoCount++;
          else this.activeImageCount++;

          await this.launchUpload(item);
          launched = true;
        }
      }

      if (!launched) {
        // All pending items are blocked by concurrency limits; wait
        await delay(500);
      } else {
        // Brief yield to let uploads start
        await delay(100);
      }
    }

    await this.checkBatchCompletions(bucketName);
    await this.queue.clearCompleted();
  }

  private async launchUpload(item: UploadMediaItem): Promise<void> {
    const id = item.id!;

    await this.queue.updateStatus(id, UploadStatus.Uploading);

    const tusUpload = new tus.Upload(item.file, {
      endpoint: `${environment.supabaseUrl}/storage/v1/upload/resumable`,
      retryDelays: [],
      headers: {
        Authorization: `Bearer ${this.authService.accessToken}`,
        'x-upsert': 'true',
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: item.metadata.bucketName,
        objectName: item.metadata.objectName,
        contentType: item.metadata.fileType,
        cacheControl: '3600',
      },
      chunkSize: 6 * 1024 * 1024,
      onError: async (error: Error | tus.DetailedError) => {
        // Skip processing for intentionally cancelled uploads
        if (this.cancelledIds.has(id)) {
          this.cancelledIds.delete(id);
          this.activeUploads.delete(id);
          return;
        }

        this.decrementActiveCount(item);

        try {
          logger.log(error, `UploadManagerService.launchUpload -> onError (id: ${id})`);

          const retryCount = item.retryCount ?? 0;
          const maxRetries = item.maxRetries ?? 3;

          if (retryCount < maxRetries) {
            const delayMs = RETRY_DELAYS[retryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
            await delay(delayMs);

            const canRetry = await this.queue.incrementRetry(id);

            if (canRetry) return;
          }

          const response = (error as tus.DetailedError)['originalResponse'];
          const request = (error as tus.DetailedError)['originalRequest'];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const xhr = (response as any)?._xhr || (request as any)?._xhr;
          let errorMessage = 'Upload failed';

          if (xhr) {
            const statusCode = xhr.status;

            if (statusCode && statusCode >= 400 && statusCode < 500) {
              errorMessage =
                statusCode === 403
                  ? FriendlyErrorMessage.UnauthorizedError
                  : FriendlyErrorMessage.UnknownError;
            } else if (statusCode === 0) {
              errorMessage = FriendlyErrorMessage.NetworkError;
            }
          }

          await this.queue.updateStatus(id, UploadStatus.Failed, errorMessage);
        } finally {
          this.activeUploads.delete(id);
        }
      },
      onProgress: async (bytesUploaded: number, bytesTotal: number) => {
        const progress = bytesTotal > 0 ? bytesUploaded / bytesTotal : 0;

        item.progress = progress;
        // Don't await DB write on progress — fire and forget for perf
        this.queue.updateProgress(id, progress);
      },
      onSuccess: async () => {
        this.decrementActiveCount(item);

        try {
          item.uploaded = true;
          item.status = UploadStatus.Completed;
          item.progress = 1;

          await this.queue.update(item);
          await this.refreshUploads();
          await this.handlePostUpload(item);
        } catch (error) {
          logger.log(error, `UploadManagerService.onSuccess error (id: ${id})`);
        } finally {
          this.activeUploads.delete(id);
        }
      },
    });

    this.activeUploads.set(id, tusUpload);
    tusUpload.start();
  }

  private decrementActiveCount(item: UploadMediaItem): void {
    const isVideo = item.metadata.fileType.startsWith('video/');

    if (isVideo) {
      this.activeVideoCount = Math.max(0, this.activeVideoCount - 1);
    } else {
      this.activeImageCount = Math.max(0, this.activeImageCount - 1);
    }
  }

  private updateAggregateProgress(items: UploadMediaItem[]): void {
    const activeItems = items.filter(
      (i) =>
        i.status === UploadStatus.Pending ||
        i.status === UploadStatus.Uploading ||
        i.status === UploadStatus.Completed
    );

    if (activeItems.length === 0) {
      this.progressPercentage$.next(null);
      return;
    }

    const totalBytes = activeItems.reduce((acc, i) => acc + (i.fileSize ?? i.file.size), 0);
    const uploadedBytes = activeItems.reduce(
      (acc, i) => acc + (i.progress ?? 0) * (i.fileSize ?? i.file.size),
      0
    );

    const percentage = totalBytes > 0 ? (uploadedBytes / totalBytes).toFixed(2) : '0.00';

    this.progressPercentage$.next(percentage);
  }

  private async refreshUploads(items?: UploadMediaItem[]): Promise<void> {
    const allItems = items ?? (await this.queue.getAll());

    this.uploads$.next(allItems);
  }

  private async checkBatchCompletions(bucketName?: BucketName): Promise<void> {
    const items = await this.queue.getAll();
    const batchIds = new Set(items.map((i) => i.batchId).filter(Boolean));

    for (const batchId of batchIds) {
      if (!batchId) continue;

      const batchItems = items.filter((i) => i.batchId === batchId);
      const allDone = batchItems.every(
        (i) =>
          i.status === UploadStatus.Completed ||
          i.status === UploadStatus.Failed ||
          i.status === UploadStatus.Cancelled
      );

      if (allDone && batchItems.length > 0) {
        const batch: UploadBatch = {
          id: batchId,
          totalFiles: batchItems.length,
          completedFiles: batchItems.filter((i) => i.status === UploadStatus.Completed).length,
          failedFiles: batchItems.filter((i) => i.status === UploadStatus.Failed).length,
          totalBytes: batchItems.reduce((acc, i) => acc + (i.fileSize ?? 0), 0),
          uploadedBytes: batchItems
            .filter((i) => i.status === UploadStatus.Completed)
            .reduce((acc, i) => acc + (i.fileSize ?? 0), 0),
          startedAt: Math.min(...batchItems.map((i) => i.addedAt ?? 0)),
          bucketName: bucketName ?? batchItems[0].metadata.bucketName,
          memoryId: batchItems[0].metadata.id,
        };

        this.batchCompleted$.next(batch);

        if (!this.isOnUploadOriginPage(batch.bucketName)) {
          await this.notifyBatchComplete(batch);
        }
      }
    }
  }

  private isOnUploadOriginPage(bucketName: BucketName): boolean {
    const router = this.injector.get(Router);
    const url = router.url;

    switch (bucketName) {
      case 'memory-asset-uploads':
        return url.startsWith('/memories/memory/') && url.includes('/assets');
      case 'vault-asset-uploads':
        return url.startsWith('/lyfe-vault/vault');
      case 'profile-picture-uploads':
        return url.startsWith('/tabs/profile');
      default:
        return false;
    }
  }

  private getBatchNotificationConfig(batch: UploadBatch): {
    navigatePath?: string;
    label: string;
  } {
    switch (batch.bucketName) {
      case 'memory-asset-uploads': {
        const navigatePath = batch.memoryId
          ? `/memories/memory/${batch.memoryId}/assets`
          : undefined;
        return { navigatePath, label: 'media' };
      }
      case 'vault-asset-uploads':
        return { label: 'document' };
      case 'profile-picture-uploads':
        return { label: 'profile photo' };
      default:
        return { label: 'file' };
    }
  }

  private getBatchToastMessage(batch: UploadBatch, label: string): string {
    if (batch.failedFiles > 0) {
      return `${batch.completedFiles} uploaded, ${batch.failedFiles} failed.`;
    }

    if (batch.completedFiles === 1) {
      return `Your ${label} was uploaded successfully.`;
    }

    return `${batch.completedFiles} ${label} files uploaded successfully.`;
  }

  private async notifyBatchComplete(batch: UploadBatch): Promise<void> {
    const { navigatePath, label } = this.getBatchNotificationConfig(batch);
    const message = this.getBatchToastMessage(batch, label);

    if (Capacitor.isNativePlatform()) {
      await this.localNotificationService.notifyBatchComplete(
        batch.completedFiles,
        batch.failedFiles,
        navigatePath
      );
    } else {
      await this.toastService.showToast({
        message,
        color: batch.failedFiles > 0 ? 'warning' : 'success',
        duration: 5000,
        buttons: navigatePath
          ? [
              {
                text: 'View',
                side: 'end',
                handler: () => {
                  const router = this.injector.get(Router);
                  router.navigateByUrl(navigatePath);
                },
              },
            ]
          : undefined,
      });
    }
  }

  private async handlePostUpload(item: UploadMediaItem): Promise<void> {
    if (item.metadata.bucketName === 'memory-asset-uploads') {
      await this.createMemoryAsset(item);
    } else if (item.metadata.bucketName === 'profile-picture-uploads') {
      await this.updateProfilePicture(item);
    } else if (item.metadata.bucketName === 'vault-asset-uploads') {
      this.updateLyfeVaultDocument(item);
    }
  }

  private async getSignedUrl(bucketName: BucketName, path: string): Promise<string> {
    const assetRepository = this.injector.get(AssetRepositoryService);

    return assetRepository.getSignedUrl(bucketName, path);
  }

  private async createMemoryAsset(item: UploadMediaItem) {
    const memoryAssetsService = this.injector.get(MemoryAssetsService);

    await memoryAssetsService.createMemoryAssets(
      [
        {
          file_name: item.metadata.fileName,
          user_id: item.metadata.userId,
          storage_location: item.metadata.objectName,
          memory_id: item.metadata.id!,
          thumbnail_location: null,
          mime_type: item.metadata.fileType,
        },
      ],
      async (data: MemoryAsset[] | null) => {
        if (!data || data.length === 0) {
          logger.log(
            item.metadata.fileName,
            'createMemoryAsset -> empty response (possible duplicate filename)'
          );
          return;
        }

        const memoryAsset = data[0];

        // Fetch a signed URL for the full-size image as a temporary thumbnail
        // so the template renders the image immediately instead of a skeleton.
        // pollForThumbnail will swap in the optimized thumbnail when ready.
        if (memoryAsset.storage_location && !memoryAsset.mime_type?.includes('video')) {
          try {
            const signedUrl = await this.getSignedUrl(
              'memory-asset-uploads',
              memoryAsset.storage_location
            );
            memoryAsset.thumbnail_signed_url = signedUrl;
          } catch (error) {
            logger.log(error, 'createMemoryAsset -> temp thumbnail error');
          }
        }

        const currentAssets = memoryAssetsService.memoryAssets$.value?.assets || [];

        if (
          currentAssets.length === 0 ||
          currentAssets.find((asset) => asset.memory_id === item.metadata.id!)
        ) {
          memoryAssetsService.setMemoryAssets({
            assets: [...currentAssets, memoryAsset],
          });
        }

        if (memoryAsset.id) {
          memoryAssetsService.pollForThumbnail(memoryAsset.id);
        }
      }
    );
  }

  private async updateProfilePicture(item: UploadMediaItem) {
    const profileService = this.injector.get(ProfileService);
    const fileSystemService = this.injector.get(FileSystemService);

    await profileService.updateProfile(
      {
        id: item.metadata.userId,
        profile_image_path: this.supabaseClient.supabase.storage
          .from('profile-picture-uploads')
          .getPublicUrl(item.metadata.objectName).data.publicUrl,
      },
      async (data: Profile | null) => {
        if (data && data.profile_image_path && Capacitor.isNativePlatform()) {
          const bucketName: BucketName = 'profile-picture-uploads';
          const filePath = data.profile_image_path.substring(
            data.profile_image_path.indexOf(bucketName)
          );
          const directory = filePath.substring(0, filePath.lastIndexOf('/'));

          await fileSystemService.removeDirectory(directory);

          fileSystemService.saveFile(data.profile_image_path, filePath);
        }
      },
      true
    );
  }

  private updateLyfeVaultDocument(item: UploadMediaItem) {
    const lyfeVaultService = this.injector.get(LyfeVaultService);
    const fileSystemService = this.injector.get(FileSystemService);

    lyfeVaultService.updateLyfeVaultDocument(
      {
        id: item.metadata.id!,
        storage_location: item.metadata.objectName,
      },
      async (data: LyfeVaultDocument | null) => {
        if (data && Capacitor.isNativePlatform()) {
          const path = data.storage_location;
          const bucketName: BucketName = 'vault-asset-uploads';

          const signedUrl = await this.getSignedUrl(bucketName, path!);

          fileSystemService.saveFile(signedUrl, `${bucketName}/${path!}`);
        }
      },
      true
    );
  }
}
